import { useDispatch } from 'react-redux';
import React, { useCallback, useMemo } from 'react';
import { BBCommonProps, StorySettingsOfCard } from 'types/story';
import { COMPONENT_STATES } from 'common/constants';
import { updateAnswers } from 'client/actions';
import { ClientReducerState } from 'client/reducers';
import AnswerContext from 'client/components/common/BuildingBlocks/Answer/Context';
import { ChildrenWithParentState } from 'client/components/common/BuildingBlocks/ChildrenWithParentState';
import { EventProviderContextT } from 'client/components/pages/Story/EventProvider/Context';
import { LottieEvEm } from 'client/components/common/BuildingBlocks/Lottie/utils';
import { AnimationEvEm } from 'client/components/common/BuildingBlocks/animation';
import AnswerBody from './AnswerBody';

type Props = BBCommonProps &
	EventProviderContextT & {
		storyId: string;
		cardId: string;
		showResults: boolean;
		showCorrect: boolean;
		cardSettings: StorySettingsOfCard | undefined;
		answersData: ClientReducerState['user']['answers'][string];
		totalSelectedAnswers: number;
		answersMax: number;
	};

const PollMultipleAnswer: React.FC<Props> = ({
	answersData,
	totalSelectedAnswers,
	cardSettings,
	answersMax,
	storyId,
	cardId,
	...props
}) => {
	const answerId = props._id;
	const answerNodeId = props.uiConfig.nodeProps.id;
	const dispatch = useDispatch();
	const isSubmitted = Boolean(answersData?.submitted);
	const isReachedAnswersMax = totalSelectedAnswers >= answersMax;
	const isSelected = answersData?.selected.includes(answerId);
	const isDisabled = props.isEditableMode || (isReachedAnswersMax && !isSelected) || isSubmitted;

	// Provide vote data to child components to display vote results (see ResultText, ResultShape)
	const answerVoteResult = useMemo(() => {
		const isShow = !props.isEditableMode && isSubmitted;
		const noVotes = !Object.keys(answersData?.votes ?? {}).length;
		const value = answersData?.votes?.[answerId] ?? (noVotes && isSelected ? 100 : 0);
		return { isShow, value, duration: 350 };
	}, [props.isEditableMode, answersData?.votes, answerId, isSubmitted, isSelected]);

	const { setStates } = props;
	const embedCode = props.uiConfig.componentProps?.other?.embedCode;
	const textContent = props.containerRef?.current?.innerText || 'MediaContent*';
	const eventListenersOnClick = props.eventListeners?.onClick;

	const onClickCapture = useCallback(
		async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
			event.stopPropagation(); // prevent nested dom nodes event handlers
			event.preventDefault(); // prevent click on nested <a> nodes

			// The problem is when you do event.stopPropagation in capture phase it cancels handler in
			// bubbling phase on the element, what breaks animation with onclick trigger,
			// so as fix — normal onclick listener is called manually:
			eventListenersOnClick?.(event);

			setStates({
				[COMPONENT_STATES.HOVER]: isSelected,
				[COMPONENT_STATES.SELECTED]: !isSelected,
			});

			if (!isSelected) {
				LottieEvEm.emit('playback', { action: 'onMultipleAnswerSelect', answerNodeId });
				AnimationEvEm.emit('playback', { action: 'onMultipleAnswerSelect', answerNodeId });
			}

			// Store answer in redux (select|deselect)
			dispatch(updateAnswers({ cardId, answerId, data: { embedCode, textContent } }));
		},
		[eventListenersOnClick, isSelected, cardId, answerId, answerNodeId, textContent, embedCode, setStates, dispatch]
	);

	const providedContext = useMemo(
		() => ({
			showResults: props.showResults,
			answerVote: answerVoteResult,
		}),
		[props.showResults, answerVoteResult]
	);

	return (
		<AnswerBody {...props} isDisabled={isDisabled} onClickCapture={isDisabled ? undefined : onClickCapture}>
			<AnswerContext.Provider value={providedContext}>
				{props.isEditableMode ? (
					props.children
				) : (
					<ChildrenWithParentState states={props.states /* change child states to answer state */}>
						{props.children}
					</ChildrenWithParentState>
				)}
			</AnswerContext.Provider>
		</AnswerBody>
	);
};

export default PollMultipleAnswer;
